import axios from "axios";
import { LS } from "@/constants/index";
import { useAppStore } from "@/store/app/slice";
import { AuthState } from "@/types/common";
import type { IUser } from "@/types/users";
import { UserLoadingStatus } from "@/types/users";
import { Environment, getApiUrl, getEnvironment } from "@/utils/getEnvironment";
import { getTenantFromUrl } from "@/utils/getTenantFromUrl";

/**
 * Handles successful authentication by:
 * 1. Setting access token in store
 * 2. Storing refresh token for localhost
 * 3. Fetching current user data
 * 4. Updating authentication state
 *
 * @param accessToken - JWT access token from auth response
 * @param refreshToken - Refresh token from auth response
 * @param tenant - Optional tenant name for registration flow
 * @returns Promise with the fetched user data
 */
export const handleSuccessfulAuthentication = async (
  accessToken: string,
  refreshToken: string,
  tenant?: string,
): Promise<void> => {
  let userTenants: string[] = [];
  let currentTenant: string = tenant ?? getTenantFromUrl() ?? "";
  let currentUser: IUser | null = null;

  if (getEnvironment() === Environment.Local && refreshToken) {
    localStorage.setItem(LS.RefreshToken, refreshToken);
  }

  const plainAxios = axios.create({
    baseURL: getApiUrl(),
    withCredentials: getEnvironment() !== Environment.Local,
  });

  try {
    const tenantsResponse = await plainAxios.get<string[]>(
      "/api/tenant/userTenants",
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (tenantsResponse.data) {
      userTenants = tenantsResponse.data;
    }
  } catch (e) {
    console.log(e);
  }

  try {
    if (!currentTenant && userTenants.length === 1) {
      currentTenant = userTenants[0];
    }

    if (currentTenant) {
      useAppStore.getState().setUserLoaded(UserLoadingStatus.Loading);
      const userResponse = await plainAxios.get<IUser>(
        "/api/auth/currentUser",
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "X-Tenant-Name": currentTenant,
          },
        },
      );
      useAppStore.getState().setUserLoaded(UserLoadingStatus.Loaded);
      currentUser = userResponse.data;
    }
  } catch (_) {
    useAppStore.getState().setUserLoaded(UserLoadingStatus.Loaded);
  }

  useAppStore.getState().setUserTenants(userTenants);
  if (currentUser) {
    useAppStore.getState().setUser(currentUser);
  } else {
    useAppStore.getState().setUserLoaded(UserLoadingStatus.Pending);
  }
  useAppStore.getState().setAccessToken(accessToken);
  useAppStore.getState().setAuthenticated(AuthState.Authenticated);
};

// Server sends snake_case `suberror` codes; map them to full translation keys
// so a renamed key or an unknown code never renders as a raw key.
const AUTH_ERROR_KEYS: Record<string, string> = {
  invalid_email_or_password: "validation.invalidEmailOrPassword",
  password_too_short: "validation.password_length",
  password_too_long: "validation.passwordTooLong",
  password_too_weak: "validation.passwordTooWeak",
  password_recently_used: "validation.passwordRecentlyUsed",
  password_banned: "validation.passwordBanned",
  password_is_invalid: "validation.passwordIsInvalid",
  user_already_exists: "validation.userAlreadyExists",
};

export const getAuthErrorKey = (suberror?: string): string =>
  (suberror && AUTH_ERROR_KEYS[suberror]) ||
  "validation.invalidEmailOrPassword";
